/************************* Global eventlisteners *************************/

export const gtcGlobalEventListeners = {
  cartRendered: 'gtc:cart:rendered',
  cartUpdated: 'gtc:cart:updated',
  cartAdd: 'gtc:cart:add',
  cartRemove: 'gtc:cart:remove',
  cartUpdate: 'gtc:cart:update',
  cartChange: 'gtc:cart:change',
  cartClear: 'gtc:cart:clear',
  produductAdded: 'gtc:product:added',
};


/************************* Async Content *************************/

/**
* Retrieves the HTML content of a page from a given URL.
* @param {string} pageUrl - The URL of the page to retrieve the content from.
* @returns {Promise<string>} - A promise that resolves to the HTML content of the page.
*/
export const getPageContentHtml = async (pageUrl) => {
  try {
      const response = await fetch(`${ pageUrl }.json`);
      const pageJson = await response.json();

      if (pageUrl.includes('policies/')) {
          const { policy: { body } = {} } = pageJson;
          return body;
      } else {
          const { page: { body_html } = {} } = pageJson;
          return body_html;
      }
  } catch (error) {
      console.error('Error: ', error);
      return '';
  }
};

/**
* Retrieves the HTML content of a specific section from a page.
* @param {string} section - The section to retrieve the HTML content for.
* @param {string} canonicalUrl - The canonical URL of the page (optional).
* @returns {Promise<string>} - The HTML content of the specified section.
*/
export const getSectionHtml = async (section, canonicalUrl) => {
  try {
      const basePath = canonicalUrl ? new URL(canonicalUrl).pathname : window.location.pathname;
      const url = basePath === '/' ? `/?sections=${ section }` : `${ basePath }/?sections=${ section }`;
      // Get page as JSON to get body_html, to inject in modal/ drawer
      const sectionJson = await (await fetch(url)).json();
      return sectionJson[section];
  } catch (error) {
      console.error('Error: ', error);
      return '';
  }
};

/**
* Retrieves the inner HTML content of a template element with the specified ID.
* @param {string} templateId - The ID of the template element.
* @returns {Promise<string>} - A promise that resolves to the inner HTML content of the template element.
*/
export const getTemplateHtml = async (templateId) => {
  const template = document.getElementById(templateId);
  return template.innerHTML;
}

/************************* Trap Focus *************************/

let gtcPreviousFocus = null;

/**
* Sets the previous focus element.
* If an element is provided, it sets the provided element as the previous focus.
* If no element is provided, it sets the currently active element as the previous focus.
*
* @param {HTMLElement} element - The element to set as the previous focus.
*/
export const gtcSetPreviousFocus = (element) => {
  if (element) {
      gtcPreviousFocus = element;
  }
  else {
      gtcPreviousFocus = document.activeElement;
  }
}

function gtcHandleTrapFocus(e) {

  const isTabPressed = (e.key === 'Tab' || e.keyCode === 9);

  if (!isTabPressed) {
      return;
  }

  const activeElement = this.activeElement || document.activeElement;


  if (e.shiftKey) { // if shift key pressed for shift + tab combination
      if (activeElement === this.firstFocusableElement) {
          this.lastFocusableElement.focus(); // add focus for the last focusable element
          e.preventDefault();
      }
  } else { // if tab key is pressed
      if (activeElement === this.lastFocusableElement) { // if focused has reached to last focusable element then focus first focusable element after pressing tab
          this.firstFocusableElement.focus(); // add focus for the first focusable element
          e.preventDefault();
      }
  }

  // if array this.focusableElements does not contain the activeElement, focus the first focusable element
  if (!this.focusableElements.includes(activeElement)) {
      this.firstFocusableElement.focus();
  }
}

/**
* Traps the focus within a specified element by restricting focusable elements to only those within the element.
* @param {HTMLElement} container - The element to trap the focus within.
*/
export const gtcTrapFocus = (container) => {

  const queryString = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  let focusableElementsInDOM = Array.from(container.querySelectorAll(queryString));

  let slotElements = Array.from(container.querySelectorAll('slot'));

  let assignedElements = slotElements.map(slot => slot.assignedElements()).flat();

  let focusableElementsInAssignedElements = assignedElements.map(assignedElement => Array.from(assignedElement.querySelectorAll(queryString))).flat();

  const focusableElements = [...focusableElementsInDOM, ...focusableElementsInAssignedElements];

  container.firstFocusableElement = focusableElements[0];
  container.lastFocusableElement = focusableElements[focusableElements.length - 1];
  container.focusableElements = focusableElements;

  // Focus the first element initially
  if (container.firstFocusableElement) {
      container.firstFocusableElement.focus();
  }

  // Listen for and trap the keyboard
  container.addEventListener('keydown', gtcHandleTrapFocus);
}

/**
* Removes the trap focus behavior from an element.
* @param {HTMLElement} element - The element from which to remove the trap focus behavior.
*/
export const gtcRemoveTrapFocus = (element) => {
  // Remove the event listener
  element.removeEventListener('keydown', gtcHandleTrapFocus);

  if (gtcPreviousFocus) {
      gtcPreviousFocus.focus();
      gtcPreviousFocus = null;
  }
}

/************************* Scrolllock *************************/

/**
* Locks the scroll and adds specified classes to the body element.
* @param {Array} classArr - An array of classes to be added to the body element.
*/

let isScrollLocked = false;


export const gtcScrollLock = (classArr) => {

  if (isScrollLocked) {
      return;
  }

  isScrollLocked = true;
  const body = document.body;
  const docEl = document.documentElement;
  const scrollbarWidth = window.innerWidth - docEl.clientWidth;
  body.style.paddingRight = `${ scrollbarWidth }px`;
  body.style.overflow = 'hidden';
  body.classList.add(...classArr);
}

/**
* Restores the scroll behavior and removes specified classes from the body element.
* @param {Array} classArr - An array of classes to be removed from the body element.
*/
export const gtcScrollUnlock = (classArr) => {
  if (!isScrollLocked) {
      return;
  }

  isScrollLocked = false;
  const body = document.body;
  body.style.paddingRight = '';
  body.style.overflow = '';
  body.classList.remove(...classArr);
}

/************************* Handleize *************************/

export const gtcHandleize = (string) => {
  return string
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-') // replace non-alphanumeric characters with hyphens
      .replace(/^-+|-+$/g, ''); // remove leading and trailing hyphens
}
/************************* Transitions *************************/

/**
*
* @param {*} element  The element to listen for transition end
* @param {*} callback The function to call when the transition ends
*/
export const gtcTransitionEnd = (element, callback) => {
  let transitionDuration = getComputedStyle(element).transitionDuration;
  transitionDuration = parseFloat(transitionDuration) * 1000; // Convert to milliseconds

  setTimeout(() => {
      callback();
  }, transitionDuration);
}

/************************* Debounce *************************/

/**
*
* @param {*} func The function to call after the delay
* @param {*} delay The delay in milliseconds before calling the function
* @returns  A function that will call the provided function after the delay
*/
export const gtcDebounce = (func, delay = 300) => {
  let debounceTimer;
  return function() {
      const context = this;
      const args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(context, args), delay);
  }
}

/************************* Throttling *************************/

/**
*
* @param {*} func The function to call after the delay
* @param {*} delay The delay in milliseconds before calling the function
* @returns  A function that will call the provided function after the delay
*/
export const gtcThrottle = (func, delay = 300) => {
  let lastCall = 0;
  return function() {
      const now = Date.now();
      if (now - lastCall < delay) {
          return;
      }
      lastCall = now;
      return func.apply(this, arguments);
  }
}

/************************* Set cookie *************************/

export function gtcSetCookie(name, value, days) {
  let expires = "";
  if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days*24*60*60*1000));
      expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "")  + expires + "; path=/";
}


/************************* Get cookie *************************/

export function gtcGetCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for(let i=0;i < ca.length;i++) {
      let c = ca[i];
      while (c.charAt(0)===' ') {
          c = c.substring(1,c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
          return c.substring(nameEQ.length,c.length);
      }
  }
  return null;
}

/************************* Color text on hover *************************/

export function gtcChangeColorText(element) {
  const dom = {
      currentColorName: element.querySelector('[data-current-color-name]')
  };

  if (!dom.currentColorName && !element.querySelector('[data-color-name]')) {
      console.error('GTCLinkedProductsColorName: Missing [data-current-color-name] element');

      currentColorName.remove();
      return;
  }

  const settings = {
      currentColorNameText: element.querySelector('[data-color-name].active')?.dataset.colorName
  };

  if (dom.currentColorName.textContent.length === 0 && settings.currentColorNameText) {
      dom.currentColorName.textContent = settings.currentColorNameText;
  }

  function changeColorName(event) {
      const colorNameElem = event.target.closest('[data-color-name]');

      if (!colorNameElem) {
          return;
      }
      dom.currentColorName.textContent = colorNameElem?.dataset.colorName ?? settings.currentColorNameText;
  }

  function changeBackColorName() {
      dom.currentColorName.textContent = settings.currentColorNameText;
  }

  function addEventListeners() {
      element.addEventListener('mouseover', changeColorName);
      element.addEventListener('mouseout', changeBackColorName);
  }

  function removeEventListeners() {
      element.removeEventListener('mouseover', changeColorName);
      element.removeEventListener('mouseout', changeBackColorName);
  }

  addEventListeners();

  return {
      reInit: function() {
          removeEventListeners();
          addEventListeners();
      }
  };
}
